import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { precoPorQuantidade } from '../lib/preco-por-quantidade';
import { uuidLocal } from '../lib/id'
import type { FaixaPrecoProduto } from '../api';

/** Subitem escolhido dentro de um passo (complemento/adicional/tamanho). */
export interface SubitemCarrinho {
  idTabelaPrecoItem: number;
  idProduto: number;
  nome?: string;
  quantidade: number;
  /** Preço unitário do subitem (já embutido no `precoUnitario` do item). */
  valor: number;
}

/** Passo do produto com os subitens selecionados pelo cliente. */
export interface PassoCarrinho {
  idProdutoPasso: number;
  descricao?: string;
  itens: SubitemCarrinho[];
  /**
   * Contribuição do passo ao preço (por unidade do item), já considerando o
   * tipo de cálculo (UNITÁRIO/FRAÇÃO/MAIOR_VALOR). Usado na integração para
   * separar o valor do item pai do total dos subitens — ver montarPayloadPedido.
   */
  valorPasso?: number;
  idProdutoPassoPai?: number
  idItemPai?: number
}

export interface ItemCarrinho {
  id: string;
  idProduto: number;
  nome: string;
  quantidade: number;
  precoUnitario: number;
  observacao?: string;
  passos?: PassoCarrinho[];
  faixasPreco?: FaixaPrecoProduto[];
  precoUnitarioBase?: number;
}

function precoDaLinha(item: ItemCarrinho, quantidade: number): number {
  const base = item.precoUnitarioBase ?? item.precoUnitario;
  return precoPorQuantidade(item.faixasPreco, quantidade, base);
}

/** Tentativa de envio do pedido: `assinatura` é o conteúdo + ponto de acesso que a `chave` identifica. */
export interface EnvioPedido {
  chave: string
  assinatura: string
  cashbackPendente?: CashbackPendente
}

/**
 * Por que o envio ficou pendente:
 * - INDISPONIVEL: o backend respondeu CASHBACK_INDISPONIVEL, e o pedido não foi gravado;
 * - SEM_RESPOSTA: rede, tempo esgotado ou 5xx; o pedido pode até ter sido gravado.
 */
export const motivosDoCashbackPendente = ['INDISPONIVEL', 'SEM_RESPOSTA'] as const

export type MotivoDoCashbackPendente = (typeof motivosDoCashbackPendente)[number]

/**
 * O envio desta chave falhou e o valor pode ter saído do saldo sem a resposta chegar. O reenvio usa este
 * valor, e não o saldo reconsultado, para o corpo sair igual e a chave se repetir — só ela reaproveita
 * esse débito (ou devolve o pedido já gravado). Fica no carrinho para valer também depois de recarregar.
 */
export interface CashbackPendente {
  valorCashback: number
  /** Só vale para o cliente que fez a tentativa. */
  idUsuario: number
  motivo: MotivoDoCashbackPendente
}

interface CarrinhoState {
  idConfiguracaoCardapio: number | null;
  itens: ItemCarrinho[];
  observacaoGeral: string;
  /** Epoch ms da última modificação — base da validade de 24h. */
  atualizadoEm?: number;
  /**
   * Carrinho volátil (modo TOTEM): vive só em memória — não persiste nem
   * restaura do localStorage. Cada sessão no kiosk começa limpa.
   */
  volatil?: boolean;
  /** Persiste com o carrinho para a nova tentativa após reload reusar a chave de idempotência. */
  envioPedido?: EnvioPedido
}

/** Resumo do envio para comparar tentativas sem gravar nome, CPF e endereço em texto claro no localStorage. */
export function assinarEnvio(dados: unknown): string {
  const texto = JSON.stringify(dados) ?? ''
  const resumo = (semente: number): string => {
    let h1 = 0xdeadbeef ^ semente
    let h2 = 0x41c6ce57 ^ semente
    for (let i = 0; i < texto.length; i += 1) {
      const c = texto.charCodeAt(i)
      h1 = Math.imul(h1 ^ c, 2654435761)
      h2 = Math.imul(h2 ^ c, 1597334677)
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)
    return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, '0')
  }
  return `${resumo(1)}${resumo(2)}`
}

/** Mesma assinatura reusa a chave (toque repetido, nova tentativa após erro); qualquer mudança gera outra. */
export function chaveParaEnvio(
  envioAtual: EnvioPedido | undefined,
  assinatura: string,
  gerar: () => string = uuidLocal,
): { chave: string; nova: boolean } {
  if (envioAtual?.chave && envioAtual.assinatura === assinatura) return { chave: envioAtual.chave, nova: false }
  return { chave: gerar(), nova: true }
}

const baseLs = 'cardapio-sischef:carrinho';

/**
 * Validade do carrinho persistido. Depois disso o conteúdo é descartado no
 * load — produtos podem ter mudado de preço, saído do cardápio etc.
 */
const VALIDADE_CARRINHO_MS = 24 * 60 * 60 * 1000;

function chavePorLoja(idConfig: number | null): string {
  return `${baseLs}:${idConfig ?? 'sem-loja'}`;
}

function estadoVazio(idConfig: number | null): CarrinhoState {
  return { idConfiguracaoCardapio: idConfig, itens: [], observacaoGeral: '' };
}

function carregar(idConfig: number | null): CarrinhoState {
  if (typeof localStorage === 'undefined') return estadoVazio(idConfig);
  try {
    const raw = localStorage.getItem(chavePorLoja(idConfig));
    if (!raw) return estadoVazio(idConfig);
    const salvo = JSON.parse(raw) as CarrinhoState;
    const expirado =
      !salvo.atualizadoEm || Date.now() - salvo.atualizadoEm > VALIDADE_CARRINHO_MS;
    if (expirado) {
      localStorage.removeItem(chavePorLoja(idConfig));
      return estadoVazio(idConfig);
    }
    return salvo;
  } catch {
    return estadoVazio(idConfig);
  }
}

function persistir(state: CarrinhoState): void {
  state.atualizadoEm = Date.now();
  if (state.volatil) return;
  localStorage.setItem(chavePorLoja(state.idConfiguracaoCardapio), JSON.stringify(state));
}

const carrinhoSlice = createSlice({
  name: 'carrinho',
  initialState: { idConfiguracaoCardapio: null, itens: [], observacaoGeral: '' } as CarrinhoState,
  reducers: {
    abrirLoja(_state, action: PayloadAction<{ idConfiguracaoCardapio: number; volatil?: boolean }>) {
      const { idConfiguracaoCardapio, volatil } = action.payload;
      if (volatil) return { ...estadoVazio(idConfiguracaoCardapio), volatil: true };
      return carregar(idConfiguracaoCardapio);
    },
    adicionarItem(state, action: PayloadAction<ItemCarrinho>) {
      state.itens.push({
        ...action.payload,
        precoUnitarioBase: action.payload.precoUnitarioBase ?? action.payload.precoUnitario,
      });
      persistir(state);
    },
    duplicarItem(state, action: PayloadAction<{ id: string }>) {
      const original = state.itens.find((i) => i.id === action.payload.id);
      if (!original) return;
      const copia: ItemCarrinho = { ...original, id: uuidLocal() };
      state.itens.push(copia);
      persistir(state);
    },
    removerItem(state, action: PayloadAction<{ id: string }>) {
      state.itens = state.itens.filter((i) => i.id !== action.payload.id);
      persistir(state);
    },
    atualizarQuantidade(state, action: PayloadAction<{ id: string; quantidade: number }>) {
      const item = state.itens.find((i) => i.id === action.payload.id);
      if (!item) return;
      item.precoUnitario = precoDaLinha(item, action.payload.quantidade);
      item.quantidade = action.payload.quantidade;
      persistir(state);
    },
    reprecificarItens(state, action: PayloadAction<{ itens: ItemCarrinho[] }>) {
      state.itens = action.payload.itens;
      persistir(state);
    },
    definirObservacaoGeral(state, action: PayloadAction<string>) {
      state.observacaoGeral = action.payload;
      persistir(state);
    },
    definirObservacaoItem(state, action: PayloadAction<{ id: string; observacao: string }>) {
      const item = state.itens.find((i) => i.id === action.payload.id)
      if (!item) return
      const texto = action.payload.observacao
      item.observacao = texto.trim() ? texto : undefined
      persistir(state)
    },
    limparCarrinho(state) {
      state.itens = [];
      state.observacaoGeral = '';
      delete state.envioPedido
      persistir(state);
    },
    registrarEnvioPedido(state, action: PayloadAction<EnvioPedido>) {
      state.envioPedido = action.payload
      persistir(state)
    },
    descartarEnvioPedido(state) {
      delete state.envioPedido
      persistir(state)
    },
    marcarCashbackPendente(state, action: PayloadAction<{ chave: string } & CashbackPendente>) {
      const { chave, valorCashback, idUsuario, motivo } = action.payload
      // A chave do carrinho já é outra: o envio que voltou com o erro não é mais o atual.
      if (state.envioPedido?.chave !== chave) return
      state.envioPedido.cashbackPendente = { valorCashback, idUsuario, motivo }
      persistir(state)
    },
    esquecerCashbackPendente(state) {
      if (!state.envioPedido?.cashbackPendente) return
      delete state.envioPedido.cashbackPendente
      persistir(state)
    },
  },
});

export const {
  abrirLoja,
  adicionarItem,
  duplicarItem,
  removerItem,
  atualizarQuantidade,
  definirObservacaoGeral,
  definirObservacaoItem,
  limparCarrinho,
  reprecificarItens,
  registrarEnvioPedido,
  descartarEnvioPedido,
  marcarCashbackPendente,
  esquecerCashbackPendente,
} = carrinhoSlice.actions;
export const carrinhoReducer = carrinhoSlice.reducer;
